import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import { ButtonGroup, Button } from '@shopify/polaris';
import { useWindowSize } from '~/presentation/common';
import { cls } from '~/utils';
import { GDiv } from '..';

enum EScrollDirection {
  LEFT = 'left',
  RIGHT = 'right',
}

export interface GScrollWrapperWithButtonRef {
  triggerHoverOut: () => void;
}

interface GScrollWrapperWithButtonProps {
  scrollWrapperId?: string;
  children: React.ReactNode;
  scrollAmount?: number;
  onButtonGroupClick?: () => void;
}

export const GScrollWrapperWithButton = forwardRef<
  GScrollWrapperWithButtonRef,
  GScrollWrapperWithButtonProps
>(({ scrollWrapperId, children, scrollAmount = 300, onButtonGroupClick }, ref) => {
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const { isMobileView } = useWindowSize();

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const scrollButtonsRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => {
    return {
      triggerHoverOut() {
        setIsHovered(false);
      },
    };
  }, []);

  const isScrollable = canScrollLeft || canScrollRight;
  // Touch devices scroll by swiping, and emulated mouseenter on tap would surface these buttons unintentionally
  const isShowScrollButtons = !isMobileView && isScrollable;

  const handleScroll = (direction: EScrollDirection): void => {
    if (scrollWrapperRef.current) {
      const newPosition =
        direction === EScrollDirection.LEFT
          ? Math.max(0, scrollPosition - scrollAmount)
          : scrollPosition + scrollAmount;

      scrollWrapperRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
    }
  };

  const updateScrollButtons = (): void => {
    if (!scrollWrapperRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollWrapperRef.current;
    setScrollPosition(scrollLeft);
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
    const wrapper = scrollWrapperRef.current;
    const container = containerRef.current;
    if (!wrapper || !container) return;

    const abortController = new AbortController();
    const signal = abortController.signal;

    wrapper.addEventListener('scroll', updateScrollButtons, { signal });

    const resizeObserver = new ResizeObserver(() => {
      updateScrollButtons();
    });
    resizeObserver.observe(container);

    return () => {
      abortController.abort();
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        id={scrollWrapperId}
        ref={scrollWrapperRef}
        className="overflow-x-auto scrollbar-hide scroll-smooth flex flex-nowrap"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex-1">{children}</div>

        {isShowScrollButtons && <div className="w-[72px] flex-shrink-0" />}
      </div>

      {isShowScrollButtons && (
        <GDiv
          ref={scrollButtonsRef}
          className={cls(
            'absolute right-4 top-0 bottom-0 z-[21] flex items-center justify-end transition-opacity duration-200 opacity-0 overflow-hidden',
            {
              'opacity-100 bg-white shadow-[calc(var(--p-space-500)*-1)_0_var(--p-space-500)_0_var(--p-color-bg-surface)]':
                isHovered,
              'shadow-none': !canScrollRight,
            },
          )}
          onClick={onButtonGroupClick}
        >
          <ButtonGroup variant="segmented">
            <Button
              disabled={!canScrollLeft}
              icon={ChevronLeftIcon}
              accessibilityLabel="Scroll left"
              onClick={() => handleScroll(EScrollDirection.LEFT)}
            />
            <Button
              disabled={!canScrollRight}
              icon={ChevronRightIcon}
              accessibilityLabel="Scroll right"
              onClick={() => handleScroll(EScrollDirection.RIGHT)}
            />
          </ButtonGroup>
        </GDiv>
      )}

      {canScrollRight && (
        <div className="absolute right-0 -top-1 -bottom-1 w-16 bg-gradient-to-l from-white via-white to-transparent pointer-events-none rounded-tr-2xl rounded-br-2xl" />
      )}
    </div>
  );
});

GScrollWrapperWithButton.displayName = 'GScrollWrapperWithButton';
